import { useMemo, useEffect } from 'react'

import useAttributes from '../useAttributes'
import { resort, reorder } from '@aytm/app/insights/dashboard/lib/util'
import { combineSplitGroups } from '@aytm/app/insights/dashboard/helpers/itemsTransformation'
import { forLetterStore } from '@aytm/app/insights/dashboard/lib/letters'
import { ItemType, SubjectType } from '@aytm/app/insights/dashboard/models/types.ClientDTO'
import { InsightsDashboardPoolState } from '@aytm/app/insights/dashboard/duckness/ducks/types'
import usePool from '@duckness/use-pool'
import InsightsDashboardPool from '@aytm/app/insights/dashboard/duckness/InsightsDashboardPool'
import { stateSelector, shouldUpdate, type StateSelectorParams } from './selectors'
import { makeSortOrderOnlyEventAction } from '@aytm/app/insights/dashboard/duckness/ducks/EventDuck/EventDuck.actionCreators'
import { poolDispatch } from '@aytm/app/insights/dashboard/duckness/poolDispatch'
import { itemsOrderChanged } from '@aytm/app/insights/dashboard/helpers/items'

export type UsePropsOptions = {
  enableSort?: boolean
  fixPinned?: boolean
  sourceItems?: { waveUid?: string; uid: string }[]
}

export type UsePropsReturnType = {
  items?: ItemType[]
  showData?: boolean
  showMetadata?: boolean
  showChart?: boolean
  showTimeline?: boolean
}

const getWaveUids = (subjects: Record<string, SubjectType>) =>
  Object.values(subjects).reduce((acc, subject) => {
    acc.add(subject.waveUid)
    return acc
    // eslint-disable-next-line immutablejs/no-native-map-set
  }, new window.Set<string>())

const useProps = ({ sourceItems = null }: UsePropsOptions = {}): UsePropsReturnType => {
  // Attributes info
  const { sortByAttribute, sortByKey, sortByMethod, getMetrics } = useAttributes()

  // Store properties
  const {
    dragInfoDraggedUid,
    data,
    subjects,
    hiddenItems,
    block,
    blockItems,
    sortOrder,
    currentSplit,
    letterStore,
    showData,
    showMetadata,
    showChart,
    showTimeline,
    enableSort,
    fixPinned
  } = usePool<typeof InsightsDashboardPool, StateSelectorParams, InsightsDashboardPoolState>(
    InsightsDashboardPool,
    stateSelector,
    shouldUpdate
  )

  const waveUids = useMemo(() => getWaveUids(subjects), [subjects])

  /** Prepare items source */
  const baseItems = useMemo(() => {
    const schemaItems = blockItems?.toJS() || []
    let result = sourceItems
      ? sourceItems.map(
          sItem =>
            schemaItems.find(bItem => sItem.uid === bItem.uid) || {
              uid: sItem.uid,
              waveUid: sItem.waveUid,
              pinned: false,
              letter: null
            }
        )
      : schemaItems
    result = result.filter(item => item && waveUids.has(item.waveUid || ''))
    result = currentSplit ? combineSplitGroups(subjects, result) : result
    return result
  }, [blockItems, currentSplit, sourceItems, subjects, waveUids])

  /** Get current method */
  const currentMethod = useMemo(
    () => sortByMethod || getMetrics(sortByAttribute)?.id,
    [getMetrics, sortByAttribute, sortByMethod]
  )

  const itemLetterMapper = useMemo(
    () => forLetterStore(letterStore || {}).getItemLetterMapper(block),
    [block, letterStore]
  )

  const sortedItems = useMemo(() => {
    const shouldUseResort = enableSort && !currentSplit
    const result = dragInfoDraggedUid
      ? baseItems
      : shouldUseResort
      ? resort({ items: baseItems, data, sortByKey, sortByMethod: currentMethod, sortOrder })
      : reorder({ items: baseItems, subjects, separate: fixPinned })
    return result.map(itemLetterMapper)
  }, [
    baseItems,
    currentMethod,
    currentSplit,
    data,
    dragInfoDraggedUid,
    enableSort,
    fixPinned,
    itemLetterMapper,
    sortByKey,
    sortOrder,
    subjects
  ])

  const items = useMemo(
    () =>
      sortedItems.filter(
        (v: ItemType) => !hiddenItems.includes(v.uid) && !(v.waveUid && hiddenItems.includes(v.waveUid))
      ),
    [sortedItems, hiddenItems]
  )

  useEffect(() => {
    if (itemsOrderChanged(sortedItems, baseItems)) {
      poolDispatch(makeSortOrderOnlyEventAction(sortedItems))
    }
  }, [sortedItems, baseItems, sourceItems])

  return {
    items,
    showData,
    showMetadata,
    showTimeline,
    showChart
  }
}

export default useProps
